/**
 * A transaction's receipts (v9.0.0): add one from the phone, look at it, save
 * or share it, delete it.
 *
 * HOW A RECEIPT IS SHOWN, and why (docs/SECURITY.md §12, "cookieless serving"):
 *   - The bytes come from `receiptApi.file`, the one request the server answers
 *     for them. Nothing in the app ever points a link, an <img src> or an
 *     <iframe> at the receipt's URL — the server would refuse it anyway.
 *   - A PHOTO is shown in an <img> from a blob: URL. An <img> never runs script,
 *     whatever the file holds, and the blob is typed from the server's own list.
 *   - A PDF is never rendered inside Ballast. "Save or share" hands it to the
 *     phone (the share sheet, or a download), whose viewer is outside the app.
 *   - Every blob: URL is revoked when the sheet closes.
 *
 * TWO TAPS where iOS needs them: the first fetches, the second shares. Safari
 * only lets `navigator.share` run straight from a tap, and a network fetch in
 * between can use that permission up.
 *
 * Deleting is two taps as well ("Delete" arms, "Delete for good" deletes) —
 * the same pattern as Mark complete, because it cannot be undone.
 */

import { ApiError, receiptApi, type ApiReceipt, type ApiTransaction } from '../lib/api';
import { trapFocus } from '../lib/dialog-trap';
import {
  canAddReceipt,
  isPhoto,
  RECEIPT_ACCEPT,
  RECEIPT_TYPES,
  receiptFilename,
  receiptLine,
  tooLargeText,
  transactionAmountText,
  transactionDateText,
} from '../lib/receipt-copy';
import { prepareForUpload } from '../lib/receipt-image';

export interface ReceiptSheetOptions {
  entityId: string;
  transaction: ApiTransaction;
  receiptsEnabled: boolean;
  limits: { maxBytes: number; perTransaction: number };
  /** The transaction's receipts changed (one added or deleted). */
  onChanged: (t: ApiTransaction) => void;
  /** The session ended mid-sheet. */
  onSignedOut: () => void;
  onDismiss: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/** The share sheet when the phone has one for files; otherwise a download. */
async function saveOrShare(blob: Blob, filename: string): Promise<void> {
  const file = new File([blob], filename, { type: blob.type });
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return;
    } catch (err) {
      // The operator closing the share sheet is not an error.
      if (err instanceof DOMException && err.name === 'AbortError') return;
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function createReceiptSheet(opts: ReceiptSheetOptions): HTMLElement {
  const t = { ...opts.transaction, receipts: [...opts.transaction.receipts] };
  const urls = new Set<string>();

  const backdrop = el('div', 'sheet-backdrop');
  const sheet = el('div', 'sheet receipt-sheet');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'receipt-title');

  const title = el('h2', 'sheet-title', t.name);
  title.id = 'receipt-title';
  const sub = el(
    'p',
    'sheet-sub',
    `${transactionAmountText(t)} · ${transactionDateText(t.date)} · ${t.account}`,
  );

  const off = opts.receiptsEnabled
    ? null
    : el(
        'p',
        'sheet-note soft',
        'Adding receipts is switched off until the receipts key is set up on the server.',
      );

  const list = el('ul', 'receipt-list');
  list.setAttribute('aria-label', 'Receipts');
  const status = el('p', 'sheet-status soft');
  status.setAttribute('role', 'status');
  const message = el('p', 'sheet-message');
  message.setAttribute('role', 'alert');

  // The picker. A real <input type=file> inside a <label> styled as the
  // button: on an iPhone that offers Photo Library, Take Photo and Choose File.
  const add = el('label', 'btn-primary receipt-add');
  const addText = el('span', undefined, 'Add a receipt');
  const input = el('input', 'visually-hidden');
  input.type = 'file';
  input.accept = RECEIPT_ACCEPT;
  input.setAttribute('aria-label', `Add a receipt to ${t.name}`);
  add.append(addText, input);

  const actions = el('div', 'sheet-actions');
  const done = el('button', 'btn-quiet', 'Done');
  done.type = 'button';
  actions.append(done);

  function close() {
    for (const url of urls) URL.revokeObjectURL(url);
    urls.clear();
    opts.onDismiss();
  }
  done.addEventListener('click', close);

  function failed(err: unknown, fallback: string) {
    if (err instanceof ApiError && err.status === 401) return opts.onSignedOut();
    status.textContent = '';
    message.textContent = err instanceof ApiError ? err.message : fallback;
  }

  function renderItem(r: ApiReceipt): HTMLElement {
    const li = el('li', 'receipt-item');
    li.dataset.receiptId = r.id;
    const label = el('span', 'receipt-item-label', receiptLine(r));
    const row = el('div', 'receipt-item-actions');

    const open = el(
      'button',
      'btn-quiet receipt-open',
      isPhoto(r.contentType) ? 'View' : 'Open PDF',
    );
    open.type = 'button';
    const del = el('button', 'btn-quiet receipt-delete', 'Delete');
    del.type = 'button';
    del.setAttribute('aria-label', `Delete this ${isPhoto(r.contentType) ? 'photo' : 'PDF'}`);
    row.append(open, del);
    li.append(label, row);

    let blob: Blob | null = null;
    open.addEventListener('click', async () => {
      message.textContent = '';
      if (blob) return void saveOrShare(blob, receiptFilename(r));
      // Never build a Blob of a type the client does not know.
      if (!RECEIPT_TYPES.includes(r.contentType)) {
        message.textContent = "This receipt can't be shown.";
        return;
      }
      open.disabled = true;
      status.textContent = 'Opening…';
      try {
        blob = await receiptApi.file(r);
      } catch (err) {
        open.disabled = false;
        return failed(err, "Couldn't open the receipt. Check your connection.");
      }
      open.disabled = false;
      status.textContent = '';
      // The fetch is done; the next tap is a fresh user gesture for the share sheet.
      open.textContent = 'Save or share';
      if (isPhoto(r.contentType)) {
        const url = URL.createObjectURL(blob);
        urls.add(url);
        const img = el('img', 'receipt-preview');
        img.alt = `Receipt photo for ${t.name}`;
        img.src = url;
        li.append(img);
      } else {
        status.textContent = 'Ready — tap Save or share to open it on your phone.';
      }
    });

    let armed = false;
    del.addEventListener('click', async () => {
      message.textContent = '';
      if (!armed) {
        armed = true;
        del.textContent = 'Delete for good';
        del.classList.add('is-armed');
        return;
      }
      del.disabled = true;
      open.disabled = true;
      status.textContent = 'Deleting…';
      try {
        await receiptApi.remove(r.id);
      } catch (err) {
        del.disabled = false;
        open.disabled = false;
        return failed(err, "Couldn't delete it. Nothing changed — try again.");
      }
      t.receipts = t.receipts.filter((x) => x.id !== r.id);
      opts.onChanged({ ...t, receipts: [...t.receipts] });
      renderList();
      status.textContent = 'Receipt deleted.';
      (list.querySelector<HTMLButtonElement>('.receipt-open') ?? done).focus();
    });
    return li;
  }

  function renderList() {
    list.replaceChildren(...t.receipts.map(renderItem));
    if (t.receipts.length === 0) list.append(el('li', 'soft receipt-empty', 'No receipts yet.'));
    add.hidden = !canAddReceipt(t, { ...opts.limits, receiptsEnabled: opts.receiptsEnabled });
  }

  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    message.textContent = '';
    input.disabled = true;
    add.classList.add('is-busy');
    try {
      status.textContent = 'Preparing…';
      const prepared = await prepareForUpload(file);
      const tooLarge = tooLargeText(prepared.blob.size, opts.limits.maxBytes);
      if (tooLarge) {
        status.textContent = '';
        message.textContent = tooLarge;
        return;
      }
      status.textContent = 'Uploading…';
      const { receipt } = await receiptApi.upload(opts.entityId, t.txnKey, prepared.blob);
      t.receipts = [...t.receipts, receipt];
      opts.onChanged({ ...t, receipts: [...t.receipts] });
      renderList();
      status.textContent = 'Receipt added.';
    } catch (err) {
      failed(err, "Couldn't upload. Nothing was saved — check your connection and try again.");
    } finally {
      input.disabled = false;
      add.classList.remove('is-busy');
    }
  });

  sheet.append(title, sub);
  if (off) sheet.append(off);
  sheet.append(list, add, status, message, actions);
  backdrop.append(sheet);
  renderList();
  trapFocus({ backdrop, sheet, onDismiss: close });
  queueMicrotask(() =>
    (add.hidden ? (list.querySelector<HTMLButtonElement>('button') ?? done) : input).focus(),
  );
  return backdrop;
}
